/**
 * A problem somebody reported, and everything the browser knew at the time.
 *
 * WRITTEN BECAUSE OF A REPORT WE COULD NOT ANSWER. A viewer's video pane read
 * "Twitch's player could not load" and there was nothing to look at: the
 * failure is a third-party script that never loaded, so nothing reaches the
 * server, and the app reported no client errors anywhere. The whole of the
 * available evidence was the sentence the viewer typed into chat.
 *
 * So the description is the smallest part of this schema. `diagnostics` is
 * the part that answers questions — it would have settled that report in one
 * field, `twitch_embed_loaded: false`, without anybody guessing about
 * whitelists. A report form that collects only prose collects only the
 * confusion.
 *
 * NOTHING HERE IS COLLECTED UNTIL SOMEBODY PRESSES SEND. The error buffer
 * lives in the tab and is read once, at that moment; the screenshot is taken
 * by the viewer, from a browser prompt they answer themselves. This is a
 * report, not telemetry.
 */
import { z } from 'zod';

/** What the page can say about itself, gathered when the form is submitted. */
export const BugDiagnostics = z.object({
  /** The room's path. Not the query string — see the note in `diagnostics.ts`. */
  path: z.string().max(400),
  user_agent: z.string().max(400),
  /** `1512x828`, which answers most "it looks wrong" reports on its own. */
  viewport: z.string().max(40),
  /**
   * Whether `window.Twitch.Player` exists.
   *
   * The single most useful bit in here. False means the embed script was
   * blocked, which is a script blocker or an extension every time, and it is
   * invisible to the viewer because chat keeps working without it.
   */
  twitch_embed_loaded: z.boolean(),
  /**
   * Third-party cookies, tested rather than assumed.
   *
   * The other half of the same family of reports: the player loads, plays,
   * and serves ads to a subscriber, because it cannot see who they are.
   */
  cookies_enabled: z.boolean(),
  storage_access: z.boolean(),
  /** One line per pane: `xqc: unavailable`. */
  panes: z.array(z.string().max(160)).max(40),
  /** Recent uncaught errors and rejections, oldest first. */
  errors: z.array(z.string().max(600)).max(25),
  room_id: z.string().max(80).nullable(),
  app_version: z.string().max(80),
});

export type BugDiagnosticsShape = z.infer<typeof BugDiagnostics>;

export const BugReportInput = z.object({
  description: z.string().min(1).max(2000),
  diagnostics: BugDiagnostics,
  /**
   * A PNG data URL, or null.
   *
   * Sent inline rather than through a signed upload like `assets`. That two
   * step dance exists because artwork is tens of megabytes and would make
   * every upload a Cloud Run memory problem; one screenshot of one tab is
   * under a megabyte, and a bug report is not a hot path. The cap is
   * generous and enforced again at the route.
   */
  screenshot: z.string().max(4_000_000).nullable(),
});

export const BugReport = z.object({
  id: z.string(),
  description: z.string(),
  /** Null for a viewer who was not signed in. Reports are open to everyone. */
  uid: z.string().nullable(),
  handle: z.string().nullable(),
  diagnostics: BugDiagnostics,
  /** A signed URL, minted per read. Null when no screenshot was attached. */
  screenshot_url: z.string().nullable(),
  created_at: z.string(),
});

export type BugReportShape = z.infer<typeof BugReport>;
